import type { ReactNode } from "react";

import { Alert } from "@/components/Alert";
import { MoonMark } from "@/components/stickers";
import { useConfig } from "@/lib/useConfig";
import { cn } from "@/lib/utils";

// Page structure from zeroslop.design (DESIGN.md: Layout): a frame with
// vertical grid lines at the content edges, full-width dividers with + marks,
// and bracketed mono section labels sitting on the dividers.

/** Horizontal padding inside the frame. */
export const GUTTER = "px-zsd-lg md:px-zsd-3xl";

/** Full-width line with + marks where it meets the frame's vertical lines. */
export function Divider({ label }: { label?: string }) {
  return (
    <div className="divider">
      <span aria-hidden="true" className="divider-mark">
        +
      </span>
      <span aria-hidden="true" className="divider-mark right">
        +
      </span>
      {label && <SectionLabel>{label}</SectionLabel>}
    </div>
  );
}

/** DESIGN.md component: section-label, e.g. "[ 01 / 02 ] · SETUP". Sits on a divider. */
function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <span
      aria-hidden="true"
      className="absolute z-[1] top-0 -translate-y-1/2 left-zsd-lg md:left-zsd-3xl bg-zsd-surface px-zsd-xs -ml-zsd-xs font-zsd-label-md text-zsd-label-md uppercase text-zsd-muted whitespace-nowrap"
    >
      {children}
    </span>
  );
}

const NAV = [
  { href: "/#setup", label: "Setup" },
  { href: "/#faq", label: "FAQ" },
  { href: "/fees", label: "Fees" },
];

const navLink =
  "ease inline-flex items-center min-h-zsd-control-height font-zsd-label-lg text-zsd-label-lg text-zsd-on-surface underline decoration-zsd-muted underline-offset-4 hover:text-zsd-primary hover:decoration-zsd-primary";

export function SiteHeader() {
  return (
    <header className={cn(GUTTER, "flex items-center justify-between gap-zsd-lg py-zsd-md")}>
      <a href="/" aria-label="Moonlauncher home" className="inline-flex items-center min-h-zsd-control-height">
        <MoonMark size={30} />
      </a>
      <nav aria-label="Main" className="flex items-center gap-zsd-lg md:gap-zsd-xl">
        {NAV.map((item) => (
          <a key={item.href} href={item.href} className={navLink}>
            {item.label}
          </a>
        ))}
      </nav>
    </header>
  );
}

/** Network and availability notice from /api/config. */
export function StatusBanner() {
  const config = useConfig();
  if (!config) return null;
  let message: ReactNode = null;
  if (!config.launchesEnabled) {
    message = (
      <>
        Mainnet launches open soon. Try the full flow on devnet now at{" "}
        <a className="text-zsd-on-warning" href="https://staging.moonlauncher.app">
          staging.moonlauncher.app
        </a>
        .
      </>
    );
  } else if (config.cluster === "devnet") {
    message = "This is the devnet staging site. Launches here use test SOL with no real value.";
  }
  if (!message) return null;
  return (
    <>
      <div className={cn(GUTTER, "py-zsd-lg")}>
        <Alert tone="warning">{message}</Alert>
      </div>
      <Divider />
    </>
  );
}

const LEGAL = [
  ["/earn", "Earn"],
  ["/fees", "Fees"],
  ["/terms", "Terms"],
  ["/privacy", "Privacy"],
  ["/risks", "Risks"],
  ["/llms.txt", "llms.txt"],
] as const;

export function SiteFooter({ children }: { children?: ReactNode }) {
  return (
    <footer className={cn(GUTTER, "py-zsd-3xl grid gap-zsd-xl")}>
      {children}
      <div className="flex flex-wrap items-start justify-between gap-x-zsd-3xl gap-y-zsd-lg">
        <div className="flex items-start gap-zsd-md max-w-[var(--zsd-x-zsd-layout-approval-max)]">
          <MoonMark size={20} className="shrink-0 mt-zsd-xs" />
          <p className="m-0 font-zsd-body-sm text-zsd-body-sm text-zsd-muted">
            Moonlauncher connects your AI agent to pump.fun. You sign every launch in your own wallet.
          </p>
        </div>
        <nav aria-label="Legal" className="flex flex-wrap gap-x-zsd-xl">
          {LEGAL.map(([href, label]) => (
            <a key={href} href={href} className={navLink}>
              {label}
            </a>
          ))}
        </nav>
      </div>
    </footer>
  );
}

/**
 * Page frame: header, status banner, content, footer.
 * `full` leaves sections and dividers to the page (landing); `content` and
 * `approval` wrap the children in one padded column.
 */
export function PageShell({
  children,
  width = "content",
  footer,
  banner = true,
}: {
  children: ReactNode;
  width?: "full" | "content" | "approval";
  footer?: ReactNode;
  banner?: boolean;
}) {
  return (
    <div className="frame min-h-dvh flex flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-zsd-sm focus:left-zsd-sm focus:z-10 bg-zsd-primary text-zsd-on-primary px-zsd-md py-zsd-sm rounded-zsd-sm font-zsd-label-lg text-zsd-label-lg"
      >
        Skip to content
      </a>
      <SiteHeader />
      <Divider />
      <main id="main" className="flex-1">
        {banner && <StatusBanner />}
        {width === "full" ? (
          children
        ) : (
          <div
            className={cn(
              GUTTER,
              "py-zsd-section",
              width === "approval" && "box-content mx-auto max-w-[var(--zsd-x-zsd-layout-approval-max)]",
            )}
          >
            {children}
          </div>
        )}
      </main>
      <Divider />
      <SiteFooter>{footer}</SiteFooter>
    </div>
  );
}
